import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  bankImportApi,
  type ParsedBankOperationDto,
  type ImportMappingSuggestion,
  type MappingConfidence,
  type ConfirmImportRequest,
  type StatementInfoDto,
} from '../../api/bankImport';
import { bankAccountsApi } from '../../api/bankAccounts';
import { accountsApi } from '../../api/accounts';
import { personsApi } from '../../api/persons';
import { formatMoney } from '../../utils/formatMoney';
import DataTable from '../../components/DataTable';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';

// ── Types ──

interface ReviewedOperation extends ParsedBankOperationDto {
  suggestion?: ImportMappingSuggestion;
  debitAccount: number;
  creditAccount: number;
  personId: number | null;
  confidence: MappingConfidence;
  matchReason: string;
  linkedInvoiceId: number | null;
  linkedInvoiceInfo: string | null;
  accepted: boolean;
}

type ImportStep = 'upload' | 'review' | 'confirming' | 'done';

// ── Component ──

export default function BankImport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<ImportStep>('upload');
  const [detectedFormat, setDetectedFormat] = useState('');
  const [operations, setOperations] = useState<ReviewedOperation[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [bankAccountCode, setBankAccountCode] = useState('2410');
  const [statementInfo, setStatementInfo] = useState<StatementInfoDto | null>(null);
  const [ibanWarning, setIbanWarning] = useState<string | null>(null);
  const [confirmResult, setConfirmResult] = useState<{ created: number; skipped: number; errors: string[] } | null>(null);

  // Load accounts and persons for selectors
  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: persons } = useQuery({
    queryKey: ['persons-import'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  // Screen context for AI
  const opCount = operations.length;
  const acceptedCount = operations.filter(o => o.accepted).length;
  useReportScreen(`Bankiniu operaciju importas (${opCount} irasu)`, {
    recordCount: opCount,
    filters: { formatas: detectedFormat, priimta: String(acceptedCount) },
    preview: operations.slice(0, 5).map(o =>
      `${o.documentNumber}: ${formatMoney(o.amount)} (${o.counterpartyName || 'N/A'}) [${o.confidence}]`
    ),
  });

  // ── Summary stats ──
  const summary = useMemo(() => {
    const green = operations.filter(o => o.confidence === 'High').length;
    const yellow = operations.filter(o => o.confidence === 'Medium').length;
    const red = operations.filter(o => o.confidence === 'Low').length;
    const totalInflow = operations.filter(o => o.amount > 0).reduce((s, o) => s + o.amount, 0);
    const totalOutflow = operations.filter(o => o.amount < 0).reduce((s, o) => s + Math.abs(o.amount), 0);
    return { green, yellow, red, totalInflow, totalOutflow };
  }, [operations]);

  // Bank accounts for IBAN → GL-account resolution (camt/ESIS statements
  // carry the own IBAN — the bank account no longer needs manual typing).
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-accounts-import'],
    queryFn: () => bankAccountsApi.list(),
    retry: false,
  });

  const resolveBankAccountByIban = useCallback(async (iban: string | null) => {
    setIbanWarning(null);
    if (!iban) return;
    const match = (bankAccounts ?? []).find(
      b => (b.iban ?? '').replace(/s+/g, '').toUpperCase() === iban.replace(/s+/g, '').toUpperCase());
    if (match?.glAccountNumber) {
      setBankAccountCode(String(match.glAccountNumber));
      return;
    }
    setIbanWarning(`Sąskaita pagal IBAN ${iban} nerasta — įveskite sąskaitą rankiniu`);
  }, [bankAccounts]);

  // ── File upload handler ──
  const handleFileUpload = useCallback(async (file: File) => {
    try {
      setStep('review');
      setParseErrors([]);
      setConfirmResult(null);

      const result = await bankImportApi.upload(file);

      setDetectedFormat(result.detectedFormat);
      setParseErrors(result.errors);
      setStatementInfo(result.statementInfo ?? null);
      await resolveBankAccountByIban(result.statementInfo?.accountIban ?? null);

      if (result.operations.length === 0) {
        setParseErrors(prev => [...prev, 'Faile operacijų nerasta']);
        setStep('upload');
        return;
      }

      // Match against historical data
      const matchResult = await bankImportApi.match({
        detectedFormat: result.detectedFormat,
        operations: result.operations,
      });

      // Merge operations with suggestions
      const reviewed: ReviewedOperation[] = result.operations.map(op => {
        const suggestion = matchResult.suggestions.find(s => s.rowIndex === op.rowIndex);
        return {
          ...op,
          suggestion,
          debitAccount: suggestion?.suggestedDebitAccount ?? (op.amount > 0 ? parseInt(bankAccountCode) || 2410 : 0),
          creditAccount: suggestion?.suggestedCreditAccount ?? (op.amount < 0 ? parseInt(bankAccountCode) || 2410 : 0),
          personId: suggestion?.suggestedPersonId ?? null,
          confidence: suggestion?.confidence ?? 'Low',
          matchReason: suggestion?.matchReason ?? 'Atitikmens nėra',
          linkedInvoiceId: suggestion?.linkedInvoiceId ?? null,
          linkedInvoiceInfo: suggestion?.linkedInvoiceInfo ?? null,
          accepted: suggestion?.confidence === 'High',
        };
      });

      setOperations(reviewed);
    } catch (err: any) {
      setParseErrors([err.message || 'Nepavyko apdoroti failo']);
      setStep('upload');
    }
  }, [bankAccountCode]);

  // ── Text paste handler ──
  const handleTextImport = useCallback(async (text: string, bankCode: string) => {
    try {
      setStep('review');
      setParseErrors([]);
      setConfirmResult(null);

      const result = await bankImportApi.parseAndMatch({
        content: text,
        bankCode: bankCode || undefined,
      });

      setDetectedFormat(result.detectedFormat);
      setParseErrors(result.errors);
      setStatementInfo((result as any).statementInfo ?? null);
      await resolveBankAccountByIban((result as any).statementInfo?.accountIban ?? null);

      if (result.operations.length === 0) {
        setParseErrors(prev => [...prev, 'Turinyje operacijų nerasta']);
        setStep('upload');
        return;
      }

      const reviewed: ReviewedOperation[] = result.operations.map(op => {
        const suggestion = result.suggestions.find(s => s.rowIndex === op.rowIndex);
        return {
          ...op,
          suggestion,
          debitAccount: suggestion?.suggestedDebitAccount ?? (op.amount > 0 ? parseInt(bankAccountCode) || 2410 : 0),
          creditAccount: suggestion?.suggestedCreditAccount ?? (op.amount < 0 ? parseInt(bankAccountCode) || 2410 : 0),
          personId: suggestion?.suggestedPersonId ?? null,
          confidence: suggestion?.confidence ?? 'Low',
          matchReason: suggestion?.matchReason ?? 'Atitikmens nėra',
          linkedInvoiceId: suggestion?.linkedInvoiceId ?? null,
          linkedInvoiceInfo: suggestion?.linkedInvoiceInfo ?? null,
          accepted: suggestion?.confidence === 'High',
        };
      });

      setOperations(reviewed);
    } catch (err: any) {
      setParseErrors([err.message || 'Nepavyko apdoroti turinio']);
      setStep('upload');
    }
  }, [bankAccountCode]);

  // ── Confirm handler ──
  const confirmMutation = useMutation({
    mutationFn: (req: ConfirmImportRequest) => bankImportApi.confirm(req),
    onSuccess: (result) => {
      setConfirmResult({ created: result.createdCount, skipped: result.skippedCount, errors: result.errors });
      setStep('done');
      queryClient.invalidateQueries({ queryKey: ['bank-operations'] });
    },
    onError: (err: Error) => {
      setParseErrors([err.message]);
      setStep('review');
    },
  });

  const handleConfirm = useCallback(() => {
    const acceptedOps = operations.filter(o => o.accepted);
    if (acceptedOps.length === 0) return;

    setStep('confirming');
    confirmMutation.mutate({
      bankAccountCode,
      operations: acceptedOps.map(op => ({
        date: op.date,
        documentNumber: op.documentNumber,
        amount: op.amount,
        currencyCode: op.currencyCode,
        description: op.description,
        counterpartyName: op.counterpartyName,
        counterpartyAccount: op.counterpartyAccount,
        debitAccount: op.debitAccount,
        creditAccount: op.creditAccount,
        personId: op.personId,
        bankReference: op.bankReference,
      })),
    });
  }, [operations, bankAccountCode, confirmMutation]);

  // ── Bulk actions ──
  const acceptAllGreen = useCallback(() => {
    setOperations(prev => prev.map(o =>
      o.confidence === 'High' ? { ...o, accepted: true } : o
    ));
  }, []);

  const acceptAll = useCallback(() => {
    setOperations(prev => prev.map(o => ({ ...o, accepted: true })));
  }, []);

  const toggleAccepted = useCallback((rowIndex: number) => {
    setOperations(prev => prev.map(o =>
      o.rowIndex === rowIndex ? { ...o, accepted: !o.accepted } : o
    ));
  }, []);

  const updateOperation = useCallback((rowIndex: number, field: keyof ReviewedOperation, value: any) => {
    setOperations(prev => prev.map(o =>
      o.rowIndex === rowIndex ? { ...o, [field]: value } : o
    ));
  }, []);

  // ── Drag & drop ──
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // ── Render ──

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('banking.smartBankImport')}</h1>
        <button
          className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          onClick={() => navigate('/banking')}
        >
          {t('banking.backToBanking')}
        </button>
      </div>

      {/* Upload Step */}
      {step === 'upload' && (
        <UploadStep
          onFileUpload={handleFileUpload}
          onTextImport={handleTextImport}
          dragOver={dragOver}
          setDragOver={setDragOver}
          onDrop={handleDrop}
          bankAccountCode={bankAccountCode}
          t={t}
          setBankAccountCode={setBankAccountCode}
          fileInputRef={fileInputRef}
          errors={parseErrors}
        />
      )}

      {/* Review Step */}
      {(step === 'review' || step === 'confirming') && (
        <>
          {/* Statement banner — the trust signal: whose account, which period,
              which balances (camt.052/053 + ESIS carry it). */}
          {statementInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
              {statementInfo.accountIban && (
                <span className="text-gray-800">
                  <span className="text-xs text-gray-500">Sąskaita:</span>{' '}
                  <span className="font-mono">{statementInfo.accountIban}</span>
                </span>
              )}
              {statementInfo.bankName && (
                <span className="text-gray-600">{statementInfo.bankName}</span>
              )}
              {statementInfo.periodEnd && (
                <span className="text-gray-600">
                  {statementInfo.periodStart
                    ? new Date(statementInfo.periodStart).toLocaleDateString('lt-LT')
                    : ''}–{new Date(statementInfo.periodEnd).toLocaleDateString('lt-LT')}
                </span>
              )}
              {statementInfo.balanceStart != null && (
                <span className="text-gray-600">pradžia <span className="num">{formatMoney(statementInfo.balanceStart)}</span></span>
              )}
              {statementInfo.balanceEnd != null && (
                <span className="text-gray-800 font-medium">pabaiga <span className="num">{formatMoney(statementInfo.balanceEnd)}</span></span>
              )}
              {statementInfo.accountCurrency && (
                <span className="text-xs text-gray-500">{statementInfo.accountCurrency}</span>
              )}
            </div>
          )}
          {ibanWarning && step === 'review' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800">{ibanWarning}</div>
          )}

          {/* Summary Bar */}
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-xs text-gray-500">{t('banking.total')}</div>
              <div className="text-lg font-semibold text-gray-900">{operations.length}</div>
            </div>
            <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
              <div className="text-xs text-green-700">{t('banking.highConfidence')}</div>
              <div className="text-lg font-semibold text-green-700">{summary.green}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-3 text-center">
              <div className="text-xs text-yellow-700">{t('banking.partialMatch')}</div>
              <div className="text-lg font-semibold text-yellow-700">{summary.yellow}</div>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-3 text-center">
              <div className="text-xs text-red-700">{t('banking.noMatch')}</div>
              <div className="text-lg font-semibold text-red-700">{summary.red}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-xs text-gray-500">{t('banking.accepted')}</div>
              <div className="text-lg font-semibold text-primary-700">{acceptedCount} / {operations.length}</div>
            </div>
          </div>

          {/* Inflow/Outflow summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-xs text-gray-500">{t('banking.totalInflow')}</div>
              <div className="text-lg font-semibold text-green-600 num">{formatMoney(summary.totalInflow)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-xs text-gray-500">{t('banking.totalOutflow')}</div>
              <div className="text-lg font-semibold text-red-600 num">{formatMoney(summary.totalOutflow)}</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-xs text-gray-500">{t('banking.detectedFormat')}</div>
              <div className="text-lg font-semibold text-gray-700">{detectedFormat}</div>
            </div>
          </div>

          {/* Bulk actions */}
          <div className="flex gap-2">
            <button
              className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
              onClick={acceptAllGreen}
              disabled={summary.green === 0}
            >
              {t('banking.acceptAllGreen', { count: summary.green })}
            </button>
            <button
              className="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100"
              onClick={acceptAll}
            >
              {t('banking.acceptAll')}
            </button>
            <div className="flex-1" />
            <button
              className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={acceptedCount === 0 || step === 'confirming'}
            >
              {step === 'confirming' ? t('banking.creating') : t('banking.confirmOperations', { count: acceptedCount })}
            </button>
          </div>

          {/* Parse errors */}
          {parseErrors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-sm font-medium text-yellow-800 mb-1">{t('banking.parseWarnings')}</div>
              {parseErrors.map((err, i) => (
                <div key={i} className="text-xs text-yellow-700">{err}</div>
              ))}
            </div>
          )}

          {/* Review Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            <DataTable
              columns={[
                {
                  key: 'accepted',
                  header: '',
                  render: (row) => (
                    <input
                      type="checkbox"
                      checked={row.accepted}
                      onChange={() => toggleAccepted(row.rowIndex)}
                      className="rounded border-gray-300"
                    />
                  ),
                  className: 'w-10 text-center',
                },
                {
                  key: 'date',
                  header: t('common.date'),
                  render: (row) => new Date(row.date).toLocaleDateString('lt-LT'),
                  className: 'whitespace-nowrap',
                },
                {
                  key: 'documentNumber',
                  header: t('common.docNumber'),
                  className: 'whitespace-nowrap max-w-24 truncate',
                },
                {
                  key: 'amount',
                  header: t('common.amount'),
                  render: (row) => (
                    <span className={`num font-medium ${row.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMoney(row.amount)}
                    </span>
                  ),
                  className: 'text-right whitespace-nowrap',
                },
                {
                  key: 'currencyCode',
                  header: 'Val.',
                  className: 'text-center',
                },
                {
                  key: 'counterpartyName',
                  header: t('banking.counterparty'),
                  render: (row) => (
                    <span className="text-sm" title={row.description}>
                      {row.counterpartyName || row.description?.substring(0, 30) || '-'}
                    </span>
                  ),
                  className: 'max-w-40 truncate',
                },
                {
                  key: 'debitAccount',
                  header: t('banking.debitAccountLabel'),
                  render: (row) => (
                    <AccountSelector
                      value={row.debitAccount}
                      accounts={accounts ?? []}
                      onChange={(val) => updateOperation(row.rowIndex, 'debitAccount', val)}
                    />
                  ),
                  className: 'min-w-[180px]',
                },
                {
                  key: 'creditAccount',
                  header: t('banking.creditAccountLabel'),
                  render: (row) => (
                    <AccountSelector
                      value={row.creditAccount}
                      accounts={accounts ?? []}
                      onChange={(val) => updateOperation(row.rowIndex, 'creditAccount', val)}
                    />
                  ),
                  className: 'min-w-[180px]',
                },
                {
                  key: 'personId',
                  header: t('common.person'),
                  render: (row) => (
                    <PersonSelector
                      value={row.personId}
                      persons={persons ?? []}
                      onChange={(val) => updateOperation(row.rowIndex, 'personId', val)}
                    />
                  ),
                  className: 'min-w-[150px]',
                },
                {
                  key: 'confidence',
                  header: t('banking.confidence'),
                  render: (row) => (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        row.confidence === 'High' ? 'bg-green-100 text-green-800' :
                        row.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}
                      title={row.matchReason}
                    >
                      {row.confidence === 'High' ? 'Aukštas' : row.confidence === 'Medium' ? 'Vidutinis' : 'Žemas'}
                    </span>
                  ),
                  className: 'text-center',
                },
                {
                  key: 'linkedInvoiceInfo',
                  header: 'SF',
                  render: (row) => (
                    row.linkedInvoiceInfo ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 cursor-pointer"
                        title={`Susieta SF: ${row.linkedInvoiceId}`}
                      >
                        {row.linkedInvoiceInfo}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )
                  ),
                  className: 'max-w-48 truncate',
                },
              ]}
              data={operations}
              keyFn={(row) => row.rowIndex}
              emptyMessage={t('banking.noOperations')}
              rowClassName={(row: ReviewedOperation) => {
                if (!row.accepted) return 'opacity-60';
                return row.confidence === 'High' ? 'bg-green-50/50' :
                       row.confidence === 'Medium' ? 'bg-yellow-50/50' :
                       'bg-red-50/50';
              }}
            />
          </div>
        </>
      )}

      {/* Done Step */}
      {step === 'done' && confirmResult && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="text-2xl font-bold text-green-700 mb-2">{t('banking.importComplete')}</div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-lg p-4">
                <div className="text-3xl font-bold text-green-600">{confirmResult.created}</div>
                <div className="text-sm text-gray-600">{t('banking.created')}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-3xl font-bold text-yellow-600">{confirmResult.skipped}</div>
                <div className="text-sm text-gray-600">{t('banking.skipped')}</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-3xl font-bold text-red-600">{confirmResult.errors.length}</div>
                <div className="text-sm text-gray-600">{t('banking.errors')}</div>
              </div>
            </div>
          </div>

          {confirmResult.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-sm font-medium text-red-800 mb-2">{t('banking.errors')}</div>
              {confirmResult.errors.map((err, i) => (
                <div key={i} className="text-xs text-red-700">{err}</div>
              ))}
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <button
              className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              onClick={() => {
                setStep('upload');
                setOperations([]);
                setParseErrors([]);
                setConfirmResult(null);
              }}
            >
              {t('banking.importAnotherFile')}
            </button>
            <button
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={() => navigate('/banking')}
            >
              {t('banking.viewBankingOperations')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──

function UploadStep({ onFileUpload, onTextImport, dragOver, setDragOver, onDrop, bankAccountCode, setBankAccountCode, fileInputRef, errors, t }: {
  onFileUpload: (file: File) => Promise<void>;
  onTextImport: (text: string, bankCode: string) => Promise<void>;
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  onDrop: (e: React.DragEvent) => void;
  bankAccountCode: string;
  setBankAccountCode: (v: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  errors: string[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [textMode, setTextMode] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [bankCode, setBankCode] = useState('SEB');
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLoading(true);
      onFileUpload(file).finally(() => setLoading(false));
    }
  };

  const handleTextSubmit = () => {
    if (!csvContent.trim()) return;
    setLoading(true);
    onTextImport(csvContent, bankCode).finally(() => setLoading(false));
  };

  return (
    <div className="space-y-4">
      {/* Bank account code */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" style={{ maxWidth: '400px' }}>
        <label className="block text-xs text-gray-500 mb-1">{t('banking.bankAccountCodeLabel')}</label>
        <input
          type="text"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
          value={bankAccountCode}
          onChange={(e) => setBankAccountCode(e.target.value)}
          placeholder="pvz. 2410"
        />
        <div className="text-xs text-gray-400 mt-1">{t('banking.accountNumberHint')}</div>
      </div>

      {/* Drag & drop zone */}
      {!textMode && (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            dragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <div className="space-y-3">
            <div className="text-4xl text-gray-300">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div className="text-gray-600 font-medium">
              {t('banking.dragAndDrop')}
            </div>
            <div className="text-xs text-gray-400">
              {t('banking.supportedFormats')}
            </div>
            <div className="flex gap-3 justify-center mt-4">
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                onClick={() => fileInputRef.current?.click()}
              >
                {t('banking.browseFiles')}
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setTextMode(true)}
              >
                {t('banking.pasteCsvText')}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".csv,.txt,.xml,.sta,.mt940,.940,.acc"
              onChange={handleFileChange}
            />
          </div>
          {loading && <LoadingSpinner />}
        </div>
      )}

      {/* Text paste mode */}
      {textMode && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">{t('banking.pasteBankStatement')}</h2>
            <button
              className="text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setTextMode(false)}
            >
              {t('banking.backToFileUpload')}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('banking.bankFormat')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={bankCode}
                onChange={(e) => setBankCode(e.target.value)}
              >
                <option value="">{t('banking.autoDetect')}</option>
                <option value="SEB">SEB</option>
                <option value="SWEDBANK">Swedbank</option>
                <option value="LUMINOR">Luminor</option>
                <option value="CAMT053">XML Camt.053</option>
                <option value="CAMT052">XML Camt.052</option>
                <option value="ESIS">Litas-ESIS (.acc)</option>
                <option value="MT940">MT940 (SWIFT)</option>
              </select>
            </div>
          </div>
          <textarea
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md font-mono"
            rows={10}
            value={csvContent}
            onChange={(e) => setCsvContent(e.target.value)}
            placeholder={`Įklijuokite banko išrašo tekstą čia…\n\nPavyzdys (SEB formatu):\nData;DokNr;Aprašymas;Suma;Valiuta\n15.04.2026;OP001;Mokėjimas iš kliento;500.50;EUR\n16.04.2026;OP002;Mokėjimas tiekėjui;-200.00;EUR`}
          />
          <div className="flex gap-3 mt-3">
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              onClick={handleTextSubmit}
              disabled={!csvContent.trim() || loading}
            >
              {loading ? t('banking.parsing') : t('banking.parseAndMatch')}
            </button>
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm font-medium text-red-800 mb-1">{t('banking.errors')}</div>
          {errors.map((err, i) => (
            <div key={i} className="text-xs text-red-700">{err}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountSelector({ value, accounts, onChange }: {
  value: number;
  accounts: { accountNumber: number; name: string }[];
  onChange: (val: number) => void;
}) {
  return (
    <select
      className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded-md bg-white"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
    >
      <option value={0}>-- Pasirinkite --</option>
      {accounts.map(a => (
        <option key={a.accountNumber} value={a.accountNumber}>
          {a.accountNumber} {a.name}
        </option>
      ))}
    </select>
  );
}

function PersonSelector({ value, persons, onChange }: {
  value: number | null;
  persons: { id: number; name: string }[];
  onChange: (val: number | null) => void;
}) {
  return (
    <select
      className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded-md bg-white"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
    >
      <option value="">Be asmens</option>
      {persons.map(p => (
        <option key={p.id} value={p.id}>{p.name}</option>
      ))}
    </select>
  );
}
